import styled from "styled-components";
import HomeCard from "./HomeCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobileScreen } from "@fortawesome/free-solid-svg-icons";
import { useDeviceList } from "./provider/DeviceListProvider";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Spinner from "../components/Spinner/Spinner";
import BorderlessButton from "../components/Button/BorderlessButton";
import { useUser } from "./provider/UserAuthProvider";
import { useTranslation } from "react-i18next";

const DeviceItemLayout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  width: 100%;
  padding: 20px 12px;
  gap: 10px;
  border-radius: 16px;
  &:hover {
    background-color: var(--photos-var-fill-tertiary);
  }
`;
const DeviceName = styled.span`
  font-size: 20px;
  color: var(--brand-color);
  white-space: nowrap; /* 禁止换行 */
  overflow: hidden; /* 超出部分隐藏 */
  text-overflow: ellipsis; /* 超出部分显示省略号 */
  min-width: 0; /* 让 flex 计算宽度时可以收缩 */
  text-align: left; /* 左对齐 */
`;

function DeviceItem({ name, ip, onClick }) {
  return (
    <DeviceItemLayout onClick={onClick}>
      <div className="flex flex-col gap-[8px]">
        <div className="flex flex-row gap-[12px] items-center">
          <FontAwesomeIcon icon={faMobileScreen} size="lg" className="text-brand" />
          <DeviceName>{name}</DeviceName>
        </div>

        {ip && <div className="text-brand text-[14px] ml-[28px]">{ip}</div>}
      </div>
    </DeviceItemLayout>
  );
}

function DeviceListCard() {
  const { deviceList, fetchDeviceList, loading } = useDeviceList();
  const navigate = useNavigate();

  const [showNoDeviceTips, setShowNoDeviceTips] = useState(false);
  const [openInNewTab, setOpenInNewTab] = useState(() => {
    return localStorage.getItem("openInNewTab") === "true";
  });
  const [autoRedirect, setAutoRedirect] = useState(() => {
    return localStorage.getItem("autoRedirect") === "true";
  });

  const user = useUser();
  const { t } = useTranslation();

  const isHttps = true;

  useEffect(() => {
    if (loading) return;

    if (autoRedirect && deviceList.length === 1) {
      const device = deviceList[0];
      if (isHttps) {
        if (device.connectionAddress && device.connectionAddress.length > 0) {
          const targetUrl = device.connectionAddress[0].connectUrl;
          window.location.href = targetUrl;
        }
      } else {
        navigate(`/device/${device.deviceId}`);
      }
    }
  }, [deviceList, autoRedirect, loading, navigate]);

  useEffect(() => {
    var timeoutHandle;
    if (deviceList.length === 0) {
      timeoutHandle = setTimeout(() => {
        setShowNoDeviceTips(true);
      }, 1000);
    } else {
      setShowNoDeviceTips(false);
    }

    return () => {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    };
  }, [deviceList]);

  // const isHttps = true;

  return (
    <HomeCard
      title={`📱 ${t("select_device_to_connect")}`}
      subTitle={`${user.user ? t("all_devices") : t("lan_device")}`}
      rightAction={
        <BorderlessButton
          text={t("refresh")}
          onClick={() => {
            fetchDeviceList();
          }}
        />
      }
    >
      {showNoDeviceTips && deviceList.length === 0 && (
        <div className="flex flex-col w-full items-center justify-center pt-[12px]">
          <img src="/open-domain-access-2x.png" alt="open-domain-access" />
          <h1 className="pt-[12px] font-medium text-label">{t("no_online_device")}</h1>
          <p className="text-[14px] pt-[8px] text-secondaryLabel">{t("is_open_domain_access")}</p>
        </div>
      )}
      {((!showNoDeviceTips && deviceList.length === 0) || loading) && (
        <div className="flex w-full items-center justify-center">
          <Spinner size="s" />
        </div>
      )}
      {deviceList.map((device) => {
        if (isHttps) {
          return (
            <>
              {device.connectionAddress.map((address) => {
                return (
                  <DeviceItem
                    key={device.deviceId}
                    name={device.name}
                    ip={address.connectUrl}
                    onClick={() => {
                      if (openInNewTab) {
                        window.open(address.connectUrl, "_blank");
                      } else {
                        window.location.href = address.connectUrl;
                      }
                    }}
                  />
                );
              })}
            </>
          );
        } else {
          return (
            <DeviceItem
              key={device.deviceId}
              name={device.name}
              onClick={() => {
                if (openInNewTab) {
                  window.open(`/device/${device.deviceId}`, "_blank");
                } else {
                  navigate(`/device/${device.deviceId}`);
                }
              }}
            />
          );
        }
      })}
      <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center w-full mt-[16px] gap-[12px] sm:gap-x-[24px]">
        <div
          className="flex flex-row items-center gap-[6px] cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            const newValue = !openInNewTab;
            setOpenInNewTab(newValue);
            localStorage.setItem("openInNewTab", String(newValue));
          }}
        >
          <input
            id="checkbox-newtab"
            type="checkbox"
            className="cursor-pointer w-[16px] h-[16px] accent-[var(--brand-color)]"
            checked={openInNewTab}
            onChange={() => {}} // Handle change in parent div for better click area
          />
          <label
            htmlFor="checkbox-newtab"
            className="text-primary text-[14px] cursor-pointer select-none pointer-events-none"
          >
            {t("open_in_new_tab")}
          </label>
        </div>

        <div
          className="flex flex-row items-center gap-[6px] cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            const newValue = !autoRedirect;
            setAutoRedirect(newValue);
            localStorage.setItem("autoRedirect", String(newValue));
          }}
        >
          <input
            id="checkbox-autoredirect"
            type="checkbox"
            className="cursor-pointer w-[16px] h-[16px] accent-[var(--brand-color)]"
            checked={autoRedirect}
            onChange={() => {}} // Handle change in parent div for better click area
          />
          <label
            htmlFor="checkbox-autoredirect"
            className="text-primary text-[14px] cursor-pointer select-none pointer-events-none"
          >
            {t("auto_redirect_single_device")}
          </label>
        </div>
      </div>
    </HomeCard>
  );
}

export default DeviceListCard;
